import { createFileRoute, notFound, redirect } from '@tanstack/react-router';
import { Schema } from 'effect';
import { FinishPendingCheckoutOutcome } from '@listening/api-contract/schemas';
import {
  DEFAULT_FUNNEL_FLOW,
  FUNNEL_CAMPAIGN,
  FunnelFlow,
  type FunnelStep,
  isFunnelStep,
  stepSessionRule,
} from '@/components/funnel/config/funnels';
import {
  FunnelCard,
  FunnelShell,
  FunnelSpinner,
} from '@/components/funnel/onboarding/funnel-shell';
import { OnboardingEngine } from '@/components/funnel/onboarding/onboarding-engine';
import {
  confirmPurchaseReturn,
  NO_PURCHASE_RETURN,
  purchaseReturnDeps,
  purchaseReturnScripts,
  purchaseReturnSearchFields,
} from '@/lib/checkout/purchase-return';
import { optionalCatching } from '@/lib/search-params';
import { getSession } from '@/services/get-session';
import * as m from '@/paraglide/messages';
import { seo } from '@/seo/seo';

const FunnelStepSearchSchema = Schema.toStandardSchemaV1(
  Schema.Struct({
    flow: optionalCatching(FunnelFlow),
    finish: optionalCatching(FinishPendingCheckoutOutcome),
    ...purchaseReturnSearchFields,
  }),
);

export const Route = createFileRoute('/_funnel/onboarding/$step')({
  validateSearch: FunnelStepSearchSchema,
  beforeLoad: ({ params, search }) => {
    if (!isFunnelStep(params.step)) return;
    const rule = stepSessionRule(params.step, flowFor(params.step, search));
    if (rule === 'open') return;
    if (rule === 'anonymous') return redirectAuthenticatedToApp();
    return requireAuthenticatedSession();
  },
  loaderDeps: ({ search }) => ({
    flow: search.flow,
    ...purchaseReturnDeps(search),
  }),
  loader: async ({ params, deps, abortController }) => {
    if (!isFunnelStep(params.step)) throw notFound();
    const pixels =
      params.step === 'done'
        ? await confirmPurchaseReturn(deps, abortController.signal)
        : NO_PURCHASE_RETURN;
    return { step: params.step, flow: flowFor(params.step, deps), pixels };
  },
  head: ({ loaderData }) => ({
    meta: [
      ...seo({
        title: m.meta_onboarding_title(),
        description: m.meta_onboarding_description(),
      }),
    ],
    scripts: purchaseReturnScripts(loaderData?.pixels),
  }),
  pendingComponent: OnboardingPending,
  pendingMs: 0,
  pendingMinMs: 0,
  component: OnboardingRoute,
});

async function redirectAuthenticatedToApp() {
  const check = await getSession();
  if (check._tag === 'Authenticated') throw redirect({ to: '/app/home' });
}

async function requireAuthenticatedSession() {
  const check = await getSession();
  // Only a confirmed no-session redirects. On `Unavailable` the step renders
  // anyway: ejecting a just-signed-up user to the signup step strands them —
  // their retry is a login, and logins land in the app, never back in the
  // funnel. The exposure is one step at most: moving to the next step is a
  // param change, which re-runs this guard client-side against the API.
  if (check._tag !== 'Unauthenticated') return;
  throw redirect({
    to: '/onboarding/$step',
    params: { step: 'signup' },
    replace: true,
  });
}

const flowFor = (step: FunnelStep, search: { flow?: FunnelFlow }) =>
  step === 'account' ? 'paywall-first' : (search.flow ?? DEFAULT_FUNNEL_FLOW);

function OnboardingPending() {
  return (
    <FunnelShell>
      <FunnelCard>
        <FunnelSpinner />
      </FunnelCard>
    </FunnelShell>
  );
}

function OnboardingRoute() {
  const { step, flow } = Route.useLoaderData();
  return (
    <OnboardingEngine campaign={FUNNEL_CAMPAIGN} flow={flow} step={step} />
  );
}
